import { t } from '@/i18n';
import type { ksAnalysisAxisEnum } from '@/utils/enum';
import type z from 'zod';
import { groupInOrder } from '../../_utils/group-in-order';
import type { KsAnalysisRow } from '../_models/ks-analysis';

type Args = {
  data: KsAnalysisRow[];
  analysisAxis: z.infer<typeof ksAnalysisAxisEnum> | null;
};

export type KsTableRow = {
  id: string;
  displayName: string;
  fiscalYears: {
    year: number;
    total: number | null;
  }[];
};

export const TOTAL_ROW_ID = '__total__';
const NOT_SET_GROUP_KEY = '__not_set__';

/**
 * KS 分析テーブル用のデータソースを構築する。
 *
 * - `analysisAxis === null` の場合は軸情報を持たないため、年度別の各レスポンス行を
 *   そのまま列に展開した 1 行 (合計表示行) として返す（FE での再合算は行わない）。
 * - それ以外の場合は `row.analysisAxis.id` でグルーピングする。`project` / `fund-owner`
 *   軸では BE から `id` が null で返るため、`name` をフォールバックキーとして利用する。
 */
export const getKsTableDataSource = ({
  data,
  analysisAxis,
}: Args): KsTableRow[] => {
  if (analysisAxis === null) {
    if (data.length === 0) {
      return [];
    }
    return [
      {
        id: TOTAL_ROW_ID,
        displayName: t('total-amount'),
        fiscalYears: data.map((row) => ({
          year: row.fiscalYear,
          total: row.total,
        })),
      },
    ];
  }

  const groupKey = (row: KsAnalysisRow) =>
    row.analysisAxis.id ?? row.analysisAxis.name ?? NOT_SET_GROUP_KEY;

  return groupInOrder(data, groupKey).map(([id, items]) => ({
    id,
    displayName: items[0]?.analysisAxis.name?.toString() ?? t('not-set'),
    fiscalYears: items.map((item) => ({
      year: item.fiscalYear,
      total: item.total,
    })),
  }));
};
